/**
 * MarketplaceCardForm — real in-app card payment for the marketplace cart.
 * Takes a client_secret from POST /api/cart/checkout, confirms it with Stripe Elements
 * (no redirect for cards) and then tells the backend to finalise the order.
 */
import { useState } from "react";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Lock, Loader2 } from "lucide-react";
import { apiFetch } from "./api";

import { stripePromise } from '@/lib/stripe';

function Form({ amount, orderId, paymentIntentId, onSuccess }) {
  const stripe   = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError]           = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true); setError("");

    const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
      elements,
      // Required by Stripe at confirmation, even though card payments don't redirect.
      confirmParams: { return_url: `${window.location.origin}${window.location.pathname}` },
      redirect: "if_required",
    });

    if (stripeError) {
      setError(stripeError.message || "Your card could not be charged");
      setProcessing(false);
      return;
    }

    if (paymentIntent?.status === "succeeded") {
      try {
        const res = await apiFetch("/orders/confirm", {
          method: "POST",
          body: JSON.stringify({ payment_intent_id: paymentIntentId, order_id: orderId }),
        });
        onSuccess?.(res);
      } catch (err) {
        // The charge went through; the webhook finalises it as a backstop.
        onSuccess?.({ order_id: orderId, pending_confirmation: true });
      }
      return;
    }

    setError("Payment was not completed. Please try another card.");
    setProcessing(false);
  };

  return (
    <form onSubmit={submit} data-testid="marketplace-card-form">
      <PaymentElement options={{ layout: "tabs" }} />
      {error && (
        <p style={{ color: "#a02828", fontSize: 12.5, marginTop: 10 }} data-testid="card-error">{error}</p>
      )}
      <button
        type="submit"
        disabled={processing || !stripe}
        style={{
          width: "100%", marginTop: 16, padding: "13px 18px", borderRadius: 10, border: "none",
          background: "#1e3a2a", color: "white", fontSize: 14, fontWeight: 600,
          cursor: processing ? "wait" : "pointer", display: "flex", alignItems: "center",
          justifyContent: "center", gap: 8,
        }}
        data-testid="pay-now-btn"
      >
        {processing
          ? <><Loader2 size={15} style={{ animation: "spin 1s linear infinite" }} /> Processing…</>
          : <><Lock size={14} /> Pay ${(amount || 0).toFixed(2)}</>}
      </button>
      <p style={{ fontSize: 11, color: "#8a8378", textAlign: "center", marginTop: 10 }}>
        Card details go straight to Stripe — we never see or store them.
      </p>
    </form>
  );
}

export default function MarketplaceCardForm({ clientSecret, amount, orderId, paymentIntentId, onSuccess }) {
  if (!clientSecret) return null;
  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: { theme: "flat", variables: { colorPrimary: "#1e3a2a", colorText: "#000000" } },
      }}
    >
      <Form amount={amount} orderId={orderId} paymentIntentId={paymentIntentId} onSuccess={onSuccess} />
    </Elements>
  );
}
